import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from '../../api/adminApi';
import { promptTypeLabel } from '@padcanvas/shared';
import type { ScenarioInput, ScenarioEvalRun, ScenarioEvalRunItemResult, DimensionResult, ComparisonReport, ItemDelta, ResolvedSnapshot } from '../../types/scenarioEvals';
import './ScenarioDatasetDetail.css';
import { TabStrip } from '@shared-ui/components';
import { BenchTab } from './BenchTab';
import { BenchQuestions } from './BenchQuestions';
import { BenchScorecard } from './BenchScorecard';
import { LabelModal } from './LabelForm';

type Tab = 'questions' | 'solutions' | 'scorecard' | 'inputs' | 'runs';

export default function ScenarioDatasetDetail() {
    const { datasetId = '' } = useParams();
    // Questions first: it is the altitude you arrive at — what is in here —
    // before dropping to individual solutions.
    const [tab, setTab] = useState<Tab>('questions');
    const [questionFilter, setQuestionFilter] = useState<string | undefined>();

    const { data: dataset } = useQuery({ queryKey: ['evalDataset', datasetId], queryFn: () => adminApi.getEvalDataset(datasetId), enabled: !!datasetId });
    const isBench = dataset?.kind === 'bench';

    // The dataset arrives after the first paint, so Inputs is briefly offered
    // even on a bench. Land anyone who took that offer somewhere real.
    useEffect(() => {
        if (isBench && tab === 'inputs') setTab('questions');
    }, [isBench, tab]);

    return (
        <div className="sd-page">
            <div className="sd-breadcrumb">
                <Link to="/evals?tab=scenarios">Scenario Evals</Link> / <span>{dataset?.name ?? datasetId}</span>
            </div>
            <h1>{dataset?.name ?? 'Dataset'}</h1>

            <TabStrip
                className="sd-tabs"
                ariaLabel="Dataset views"
                active={tab}
                onChange={setTab}
                tabs={[
                    { key: 'questions', label: 'Questions' },
                    // Named for its rung of the ladder — questions, then their
                    // solutions, then each solution's prompt executions.
                    { key: 'solutions', label: 'Solutions' },
                    { key: 'scorecard', label: 'Scorecard' },
                    // Scenario Inputs is the raw row list, and on a bench every
                    // one of its jobs now has a better home: capture on
                    // Questions, delete on Solutions, labelling in the row
                    // drawer beside the work. Kept for the older platform
                    // datasets, which have no bench tabs at all.
                    ...(isBench ? [] : [{ key: 'inputs' as const, label: 'Scenario Inputs' }]),
                    { key: 'runs', label: 'Runs & Compare' },
                ]}
            />

            {tab === 'questions' && (
                <BenchQuestions
                    datasetId={datasetId}
                    onOpenQuestion={(questionId) => { setQuestionFilter(questionId); setTab('solutions'); }}
                />
            )}
            {tab === 'solutions' && (
                <BenchTab datasetId={datasetId} questionFilter={questionFilter}
                    onClearQuestionFilter={() => setQuestionFilter(undefined)} />
            )}
            {tab === 'scorecard' && <BenchScorecard datasetId={datasetId} />}
            {tab === 'inputs' && !isBench && <InputsTab datasetId={datasetId} />}
            {tab === 'runs' && <RunsTab datasetId={datasetId} />}
        </div>
    );
}

// ─── Inputs tab ────────────────────────────────────────────────────────

function InputsTab({ datasetId }: { datasetId: string }) {
    const qc = useQueryClient();
    const [submissionIds, setSubmissionIds] = useState('');
    const [labeling, setLabeling] = useState<ScenarioInput | null>(null);

    const { data, isLoading } = useQuery({ queryKey: ['scenarioInputs', datasetId], queryFn: () => adminApi.listScenarioInputs(datasetId) });

    const capture = useMutation({
        mutationFn: (ids: string[]) => adminApi.captureScenarioInputs(datasetId, { submissionIds: ids }),
        onSuccess: () => { setSubmissionIds(''); qc.invalidateQueries({ queryKey: ['scenarioInputs', datasetId] }); },
    });
    const del = useMutation({
        mutationFn: (id: string) => adminApi.deleteScenarioInput(datasetId, id),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['scenarioInputs', datasetId] }),
    });

    const inputs = data?.inputs ?? [];

    return (
        <div>
            <div className="sd-capture">
                <label>Capture from production — submission IDs (comma/newline separated)</label>
                <textarea value={submissionIds} onChange={e => setSubmissionIds(e.target.value)} rows={2} placeholder="sub-123, sub-456" />
                <button className="btn btn-primary" disabled={capture.isPending || !submissionIds.trim()}
                    onClick={() => capture.mutate(submissionIds.split(/[\s,]+/).map(s => s.trim()).filter(Boolean))}>
                    {capture.isPending ? 'Capturing…' : 'Capture'}
                </button>
                {capture.data && <span className="sd-note">Captured {capture.data.captured}, skipped {capture.data.skipped}.</span>}
                {capture.error && <span className="sd-err">{(capture.error as Error).message}</span>}
            </div>

            <div className="sd-meta">{data?.labeled ?? 0} / {data?.total ?? 0} labeled</div>

            {isLoading ? <p>Loading…</p> : (
                <table className="dt sd-table">
                    <thead><tr><th>Question</th><th>Type</th><th>Source</th><th>Label</th><th></th></tr></thead>
                    <tbody>
                        {inputs.map(i => (
                            <tr key={i.id}>
                                <td className="sd-qtext">{i.question.text?.slice(0, 90) || '—'}</td>
                                <td>{i.question.questionType}</td>
                                <td><span className="se-badge">{i.source}</span></td>
                                <td>{i.label ? <span className="sd-ok">✓ labeled</span> : <span className="sd-no">unlabeled</span>}</td>
                                <td className="sd-actions">
                                    <button className="btn btn-secondary" onClick={() => setLabeling(i)}>{i.label ? 'Edit label' : 'Label'}</button>
                                    <button className="btn btn-danger" onClick={() => { if (confirm('Delete input?')) del.mutate(i.id); }}>Delete</button>
                                </td>
                            </tr>
                        ))}
                        {inputs.length === 0 && <tr><td colSpan={5} className="sd-empty">No scenario inputs — capture some above.</td></tr>}
                    </tbody>
                </table>
            )}

            {labeling && <LabelModal datasetId={datasetId} input={labeling} onClose={() => setLabeling(null)} />}
        </div>
    );
}

// ─── Runs tab ──────────────────────────────────────────────────────────

function RunsTab({ datasetId }: { datasetId: string }) {
    const qc = useQueryClient();
    const [config, setConfig] = useState('');
    const [baselineId, setBaselineId] = useState('');
    const [candidateId, setCandidateId] = useState('');
    const [viewing, setViewing] = useState<ScenarioEvalRun | null>(null);
    const [params, setParams] = useState<ScenarioEvalRun | null>(null);
    const [showStart, setShowStart] = useState(false);
    // Timestamp (ms) the in-flight scoring started, so the row can show
    // "Updating score… (started <time>)" while the synchronous score call runs.
    const [scoringStartedAt, setScoringStartedAt] = useState<number | null>(null);
    // Table filters + sort (client-side, over the loaded runs).
    const [statusFilter, setStatusFilter] = useState('');
    const [configFilter, setConfigFilter] = useState('');
    const [sort, setSort] = useState<{ key: RunSortKey; dir: 'asc' | 'desc' }>({ key: 'started', dir: 'desc' });

    const { data: cfgData } = useQuery({ queryKey: ['pipelineConfigs'], queryFn: adminApi.listPipelineConfigs });
    const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: adminApi.getSettings });
    const configs = cfgData?.configs ?? [];
    const defaultConfigName = settings?.defaultPipelineConfig;
    // Pre-select the system default pipeline config (AppSettings.defaultPipelineConfig)
    // once configs + settings load. Fall back to a config literally named
    // 'default', else the first. Admin can change the selection afterward.
    useEffect(() => {
        if (config || !configs.length) return;
        const preferred = (defaultConfigName && configs.some(c => c.name === defaultConfigName))
            ? defaultConfigName
            : (configs.find(c => c.name === 'default')?.name ?? configs[0].name);
        setConfig(preferred);
    }, [configs, config, defaultConfigName]);

    const { data } = useQuery({
        queryKey: ['scenarioRuns', datasetId],
        queryFn: () => adminApi.listScenarioRuns(datasetId),
        refetchInterval: (q) => (q.state.data?.runs?.some(r => r.status === 'running') ? 4000 : false),
    });
    const runs = data?.runs ?? [];

    // Distinct filter options derived from the loaded runs.
    const statusOptions = useMemo(() => [...new Set(runs.map(r => r.status))].sort(), [runs]);
    const configOptions = useMemo(() => [...new Set(runs.map(r => r.pipelineConfigName))].sort(), [runs]);

    const displayedRuns = useMemo(() => {
        const filtered = runs.filter(r =>
            (!statusFilter || r.status === statusFilter) &&
            (!configFilter || r.pipelineConfigName === configFilter));
        const dir = sort.dir === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => compareRuns(a, b, sort.key) * dir);
    }, [runs, statusFilter, configFilter, sort]);

    const toggleSort = (key: RunSortKey) =>
        setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

    const start = useMutation({
        mutationFn: () => adminApi.startScenarioRun({ datasetId, pipelineConfigName: config }),
        onSuccess: () => { setShowStart(false); qc.invalidateQueries({ queryKey: ['scenarioRuns', datasetId] }); },
    });
    const score = useMutation({
        mutationFn: ({ id, useJudge, forceJudge }: { id: string; useJudge: boolean; forceJudge?: boolean }) => adminApi.scoreScenarioRun(id, useJudge, forceJudge),
        onMutate: () => { setScoringStartedAt(Date.now()); },
        onSettled: () => qc.invalidateQueries({ queryKey: ['scenarioRuns', datasetId] }),
    });
    const baseline = useMutation({ mutationFn: (id: string) => adminApi.setScenarioRunBaseline(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['scenarioRuns', datasetId] }) });

    return (
        <div>
            <div className="sd-runs-header">
                <div className="sd-filters">
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                        <option value="">All statuses</option>
                        {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select value={configFilter} onChange={e => setConfigFilter(e.target.value)}>
                        <option value="">All configs</option>
                        {configOptions.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {(statusFilter || configFilter) && <span className="sd-meta">{displayedRuns.length} of {runs.length}</span>}
                </div>
                <button className="btn btn-primary" onClick={() => setShowStart(true)}>+ Start New Run</button>
            </div>

            <table className="dt sd-table">
                <thead><tr>
                    <SortTh label="Run" k="run" sort={sort} onSort={toggleSort} />
                    <SortTh label="Config" k="config" sort={sort} onSort={toggleSort} />
                    <SortTh label="Fingerprint" k="fingerprint" sort={sort} onSort={toggleSort} title="Content hash of the fully-resolved config — same hash = identical config; differs when prompts/providers/params change" />
                    <SortTh label="Started" k="started" sort={sort} onSort={toggleSort} />
                    <SortTh label="Status" k="status" sort={sort} onSort={toggleSort} />
                    <SortTh label="Score" k="score" sort={sort} onSort={toggleSort} />
                    <th>Actions</th>
                </tr></thead>
                <tbody>
                    {displayedRuns.map(r => {
                        const scoringThisRow = score.isPending && score.variables?.id === r.id;
                        return (
                            <tr key={r.id} className={`sd-run-row ${r.isBaseline ? 'sd-baseline-row' : ''}`} onClick={() => setViewing(r)} title="View results">
                                <td>{r.readableId}{r.isBaseline && <span className="sd-pill">baseline</span>}</td>
                                <td>{r.pipelineConfigName}</td>
                                <td><code className="sd-fp" title={`${r.configFingerprint}${r.snapshot?.snapshotAt ? ` · captured ${new Date(r.snapshot.snapshotAt).toLocaleString()}` : ''}`}>{r.configFingerprint.slice(0, 10)}</code></td>
                                <td className="sd-when">{r.startedAt ? new Date(r.startedAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                                <td>
                                    {scoringThisRow
                                        ? <span className="sd-scoring">{score.variables?.forceJudge ? 'Judging all answers…' : score.variables?.useJudge ? 'Updating score + judge…' : 'Updating score…'}{scoringStartedAt && <span className="sd-meta"> (started {new Date(scoringStartedAt).toLocaleTimeString()})</span>}</span>
                                        : <RunStatus run={r} />}
                                </td>
                                <td>{r.statistics ? r.statistics.aggregate.mean.toFixed(3) : <span className="sd-no">unscored</span>}</td>
                                <td className="sd-actions" onClick={e => e.stopPropagation()}>
                                    <ActionMenu items={[
                                        { label: 'Results', onClick: () => setViewing(r) },
                                        { label: 'Params', title: "View this run's full resolved configuration (prompts, providers, params)", onClick: () => setParams(r) },
                                        { label: 'Score', disabled: score.isPending, onClick: () => score.mutate({ id: r.id, useJudge: false }) },
                                        { label: 'Score + Judge', title: 'Re-score, using the LLM judge on low/medium-confidence answer dimensions', disabled: score.isPending, onClick: () => score.mutate({ id: r.id, useJudge: true }) },
                                        { label: 'Judge all answers (test)', title: 'Force the LLM judge over EVERY answer dimension, ignoring the heuristic confidence gate — for testing the judge on clean datasets the heuristic scores high-confidence', disabled: score.isPending, onClick: () => score.mutate({ id: r.id, useJudge: false, forceJudge: true }) },
                                        ...(r.isBaseline ? [] : [{ label: 'Set baseline', onClick: () => baseline.mutate(r.id) }]),
                                    ]} />
                                </td>
                            </tr>
                        );
                    })}
                    {runs.length === 0 && <tr><td colSpan={7} className="sd-empty">No runs yet — start one with “+ Start New Run”.</td></tr>}
                    {runs.length > 0 && displayedRuns.length === 0 && <tr><td colSpan={7} className="sd-empty">No runs match the current filters.</td></tr>}
                </tbody>
            </table>

            <div className="sd-compare-pick">
                <h3>Compare</h3>
                <label>Baseline<RunSelect runs={runs} value={baselineId} onChange={setBaselineId} /></label>
                <label>Candidate<RunSelect runs={runs} value={candidateId} onChange={setCandidateId} /></label>
            </div>
            {baselineId && candidateId && baselineId !== candidateId && (() => {
                const baseline = runs.find(r => r.id === baselineId);
                const candidate = runs.find(r => r.id === candidateId);
                return baseline && candidate
                    ? <CompareView datasetId={datasetId} baseline={baseline} candidate={candidate} />
                    : null;
            })()}

            {viewing && <ResultsModal run={viewing} datasetId={datasetId} onClose={() => setViewing(null)} />}
            {params && <ParamsModal run={params} onClose={() => setParams(null)} />}
            {showStart && (
                <StartRunModal
                    configs={configs} config={config} setConfig={setConfig}
                    defaultConfigName={defaultConfigName}
                    starting={start.isPending} error={start.error as Error | null}
                    onStart={() => start.mutate()} onClose={() => setShowStart(false)}
                />
            )}
        </div>
    );
}

function StartRunModal({ configs, config, setConfig, defaultConfigName, starting, error, onStart, onClose }: {
    configs: { name: string }[];
    config: string;
    setConfig: (v: string) => void;
    defaultConfigName?: string;
    starting: boolean;
    error: Error | null;
    onStart: () => void;
    onClose: () => void;
}) {
    return (
        <div className="sd-overlay" onClick={onClose}>
            <div className="sd-modal sd-startrun" onClick={e => e.stopPropagation()}>
                <h2>Start new run</h2>
                <label className="sd-field">Pipeline config
                    <select value={config} onChange={e => setConfig(e.target.value)}>
                        {configs.length === 0 && <option value="">(loading…)</option>}
                        {configs.map(c => <option key={c.name} value={c.name}>{c.name}{c.name === defaultConfigName ? ' (default)' : ''}</option>)}
                    </select>
                </label>
                {error && <p className="sd-err">{error.message}</p>}
                <div className="sd-modal-actions">
                    <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary" disabled={starting || !config} onClick={onStart}>{starting ? 'Starting…' : 'Start run'}</button>
                </div>
            </div>
        </div>
    );
}

// ─── Per-run results (expected vs actual, per dimension) ───────────────

function ResultsModal({ run, datasetId, onClose }: { run: ScenarioEvalRun; datasetId: string; onClose: () => void }) {
    const { data: itemsData, isLoading } = useQuery({ queryKey: ['scenarioRunItems', run.id], queryFn: () => adminApi.getScenarioRunItems(run.id) });
    const { data: inputsData } = useQuery({ queryKey: ['scenarioInputs', datasetId], queryFn: () => adminApi.listScenarioInputs(datasetId) });
    const inputById = new Map((inputsData?.inputs ?? []).map(i => [i.id, i]));
    const items = itemsData?.items ?? [];
    const s = run.statistics;

    return (
        <div className="sd-overlay" onClick={onClose}>
            <div className="sd-modal sd-results" onClick={e => e.stopPropagation()}>
                <h2>Results — {run.readableId}</h2>
                <p className="sd-meta">
                    {s ? `aggregate mean ${s.aggregate.mean.toFixed(3)} · scored ${s.scored} · unlabeled ${s.unlabeled}` : 'Not scored yet — close this and click Score.'}
                </p>
                {isLoading ? <p>Loading…</p> : items.length === 0 ? <p className="sd-no">No items.</p> :
                    items.map(it => <ItemResult key={it.id} item={it} input={inputById.get(it.scenarioInputId)} />)}
                <div className="sd-modal-actions"><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
            </div>
        </div>
    );
}

function ItemResult({ item, input }: { item: ScenarioEvalRunItemResult; input?: ScenarioInput }) {
    const dims = item.evalVector
        ? (Object.entries(item.evalVector).filter(([k]) => k !== 'aggregate') as [string, DimensionResult][])
        : [];
    const agg = item.evalVector?.aggregate as { weightedScore: number } | undefined;

    return (
        <div className="sd-item">
            <div className="sd-item-head">
                <span className="sd-item-q">{input?.question.text?.slice(0, 120) || item.scenarioInputId}</span>
                <span className="sd-item-right">
                    {item.submissionId && <a className="sd-sublink" href={`/submissions/${item.submissionId}`} target="_blank" rel="noreferrer">details ↗</a>}
                    {item.status === 'failed'
                        ? <span className="sd-down">failed</span>
                        : agg ? <span className="sd-item-score">{agg.weightedScore.toFixed(3)}</span> : <span className="sd-no">unscored</span>}
                </span>
            </div>
            {item.status === 'failed' && <div className="sd-err">{item.error}</div>}
            {dims.length > 0 && (
                <table className="dt sd-dim-table">
                    <thead><tr><th>Dimension</th><th>Score</th><th>Conf</th><th>Actual</th><th>Expected</th></tr></thead>
                    <tbody>
                        {dims.map(([name, d]) => {
                            const f = fmtDetail(d.details);
                            const judge = d.details?.judge as { reasoning?: string } | undefined;
                            return <tr key={name}>
                                <td>{name}</td>
                                <td className={d.score >= 0.999 ? 'sd-up' : d.score <= 0.001 ? 'sd-down' : ''}>{d.score.toFixed(2)}</td>
                                <td>{judge
                                    ? <span className="sd-conf sd-conf-judge" title={judge.reasoning || 'Scored by the LLM judge'}>⚖ judge</span>
                                    : <span className={`sd-conf sd-conf-${d.confidence ?? 'high'}`}>{d.confidence ?? 'high'}</span>}</td>
                                <td>{f.actual}</td><td>{f.expected}</td>
                            </tr>;
                        })}
                    </tbody>
                </table>
            )}
            {item.status === 'completed' && !item.evalVector && <div className="sd-no">Not scored (input may be unlabeled).</div>}
        </div>
    );
}

/** Pull "actual" and "expected" out of a dimension's heterogeneous details. */
function fmtDetail(details: Record<string, unknown>): { actual: string; expected: string } {
    const pick = (...keys: string[]) => { for (const k of keys) { const v = details[k]; if (v !== undefined && v !== null) return v; } return undefined; };
    const a = pick('predictedValue', 'predicted', 'actual', 'actualCount', 'actualErrorFraction');
    const e = pick('expectedValue', 'expected', 'expectedCount', 'expectedErrorFraction');
    const ua = details.predictedUnit ? ` (${String(details.predictedUnit)})` : '';
    const ue = details.expectedUnit ? ` (${String(details.expectedUnit)})` : '';
    // Numeric range dimensions carry { min, max } — render "7–9" (or "9" exact).
    const fmtVal = (v: unknown): string => {
        if (v && typeof v === 'object' && 'min' in v && 'max' in v) {
            const r = v as { min: number; max: number };
            return r.min === r.max ? String(r.min) : `${r.min}–${r.max}`;
        }
        return String(v);
    };
    return {
        actual: a !== undefined ? `${fmtVal(a)}${ua}` : '—',
        expected: e !== undefined ? `${fmtVal(e)}${ue}` : '—',
    };
}

function RunSelect({ runs, value, onChange }: { runs: ScenarioEvalRun[]; value: string; onChange: (v: string) => void }) {
    return (
        <select value={value} onChange={e => onChange(e.target.value)}>
            <option value="">—</option>
            {runs.map(r => <option key={r.id} value={r.id}>{r.readableId}{r.isBaseline ? ' (baseline)' : ''}</option>)}
        </select>
    );
}

type RunSortKey = 'run' | 'config' | 'fingerprint' | 'started' | 'status' | 'score';

/** Comparator for the runs table. Returns a/b ordering in ascending sense;
 *  the caller applies direction. Unscored runs sort after scored ones. */
function compareRuns(a: ScenarioEvalRun, b: ScenarioEvalRun, key: RunSortKey): number {
    switch (key) {
        case 'run': return a.readableId.localeCompare(b.readableId);
        case 'config': return a.pipelineConfigName.localeCompare(b.pipelineConfigName);
        case 'fingerprint': return a.configFingerprint.localeCompare(b.configFingerprint);
        case 'started': return (a.startedAt ?? '').localeCompare(b.startedAt ?? ''); // ISO sorts lexically
        case 'status': return a.status.localeCompare(b.status);
        case 'score': {
            const av = a.statistics?.aggregate.mean, bv = b.statistics?.aggregate.mean;
            if (av === undefined && bv === undefined) return 0;
            if (av === undefined) return 1;
            if (bv === undefined) return -1;
            return av - bv;
        }
    }
}

/** Sortable table header cell — click to sort, arrow shows active key + dir. */
function SortTh({ label, k, sort, onSort, title }: { label: string; k: RunSortKey; sort: { key: RunSortKey; dir: 'asc' | 'desc' }; onSort: (k: RunSortKey) => void; title?: string }) {
    const active = sort.key === k;
    return (
        <th className="sd-th-sort" onClick={() => onSort(k)} title={title ?? `Sort by ${label}`}>
            {label}<span className="sd-sort-ind">{active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}</span>
        </th>
    );
}

interface ActionItem { label: string; onClick: () => void; disabled?: boolean; title?: string }

/** Compact per-row "Actions ▾" dropdown — collapses several row buttons into
 *  one menu. Closes on item select or an outside click. */
function ActionMenu({ items }: { items: ActionItem[] }) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);
    return (
        <div className="sd-menu" ref={ref}>
            <button className="btn btn-secondary" onClick={() => setOpen(o => !o)}>Actions ▾</button>
            {open && (
                <div className="sd-menu-list">
                    {items.map(it => (
                        <button key={it.label} className="sd-menu-item" disabled={it.disabled} title={it.title}
                            onClick={() => { setOpen(false); it.onClick(); }}>
                            {it.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function RunStatus({ run }: { run: ScenarioEvalRun }) {
    const { data } = useQuery({
        queryKey: ['scenarioRunProgress', run.id],
        queryFn: () => adminApi.getScenarioRun(run.id),
        enabled: run.status === 'running',
        refetchInterval: run.status === 'running' ? 4000 : false,
    });
    if (run.status === 'running' && data) {
        return <span className="sd-running">running {data.progress.completed}/{data.progress.total}</span>;
    }
    return <span className={`sd-status sd-${run.status}`}>{run.status}</span>;
}

// ─── Compare / regression report ───────────────────────────────────────

function CompareView({ datasetId, baseline, candidate }: { datasetId: string; baseline: ScenarioEvalRun; candidate: ScenarioEvalRun }) {
    const { data: report, isLoading, error } = useQuery({
        queryKey: ['scenarioCompare', baseline.id, candidate.id],
        queryFn: () => adminApi.compareScenarioRuns(baseline.id, candidate.id),
    });
    const { data: inputsData } = useQuery({ queryKey: ['scenarioInputs', datasetId], queryFn: () => adminApi.listScenarioInputs(datasetId) });
    const inputById = new Map((inputsData?.inputs ?? []).map(i => [i.id, i]));

    if (isLoading) return <p>Comparing…</p>;
    if (error) return <p className="sd-err">{(error as Error).message}</p>;
    if (!report) return null;
    return <RegressionReport report={report} inputById={inputById} baseline={baseline} candidate={candidate} />;
}

function RegressionReport({ report, inputById, baseline, candidate }: { report: ComparisonReport; inputById: Map<string, ScenarioInput>; baseline: ScenarioEvalRun; candidate: ScenarioEvalRun }) {
    const { summary, axisDiff, dimensionDeltas, items } = report;
    const delta = summary.meanDelta;
    const deltaCls = delta < -0.0005 ? 'sd-down' : delta > 0.0005 ? 'sd-up' : '';

    return (
        <div className="sd-report">
            <div className="stat-cards stat-cards--auto">
                <Card label="Mean score" value={`${summary.baselineMean.toFixed(3)} → ${summary.candidateMean.toFixed(3)}`} sub={<span className={deltaCls}>{delta >= 0 ? '+' : ''}{delta.toFixed(3)} {summary.significant ? '(significant)' : ''}</span>} />
                <Card label="Regressed" value={String(summary.regressed)} cls="sd-down" />
                <Card label="Improved" value={String(summary.improved)} cls="sd-up" />
                <Card label="Correctness flips" value={String(summary.correctnessFlips)} cls={summary.correctnessFlips ? 'sd-down' : ''} />
            </div>

            <div className="sd-axis">
                <strong>What changed:</strong>
                <span className={axisDiff.configChanged ? 'sd-axis-on' : 'sd-axis-off'}>config</span>
                <span className={axisDiff.codeChanged ? 'sd-axis-on' : 'sd-axis-off'}>code</span>
                <span className={axisDiff.rubricChanged ? 'sd-axis-on' : 'sd-axis-off'}>rubric</span>
                <span className={axisDiff.inputsChanged.length ? 'sd-axis-on' : 'sd-axis-off'}>inputs ({axisDiff.inputsChanged.length})</span>
            </div>

            <h4>Dimension deltas</h4>
            <table className="dt sd-table">
                <thead><tr><th>Dimension</th><th>Baseline</th><th>Candidate</th><th>Δ</th></tr></thead>
                <tbody>
                    {dimensionDeltas.map(d => (
                        <tr key={d.dimension}>
                            <td>{d.dimension}</td><td>{d.baselineMean.toFixed(3)}</td><td>{d.candidateMean.toFixed(3)}</td>
                            <td className={d.delta < -0.0005 ? 'sd-down' : d.delta > 0.0005 ? 'sd-up' : ''}>{d.delta >= 0 ? '+' : ''}{d.delta.toFixed(3)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <ConfigDiff baseline={baseline} candidate={candidate} />

            <h4>Per-item ({items.length}) — worst regressions first</h4>
            <table className="dt sd-table">
                <thead><tr><th>Input</th><th>Base</th><th>Cand</th><th>Δ</th><th>Moved</th><th>Flip</th></tr></thead>
                <tbody>
                    {items.map(it => <ItemRow key={it.scenarioInputId} it={it} input={inputById.get(it.scenarioInputId)} />)}
                </tbody>
            </table>
        </div>
    );
}

function ItemRow({ it, input }: { it: ItemDelta; input?: ScenarioInput }) {
    const cls = it.status === 'regressed' ? 'sd-down' : it.status === 'improved' ? 'sd-up' : '';
    const qText = input?.question.text?.trim();
    return (
        <tr>
            <td title={it.scenarioInputId}>
                {qText
                    ? <span className="sd-item-q">{qText.length > 80 ? `${qText.slice(0, 80)}…` : qText}</span>
                    : <code className="sd-fp">{it.scenarioInputId.slice(0, 8)}</code>}
                {it.inputChanged && <span className="sd-pill">edited</span>}</td>
            <td>{it.baselineScore?.toFixed(3) ?? '—'}</td>
            <td>{it.candidateScore?.toFixed(3) ?? '—'}</td>
            <td className={cls}>{it.delta !== undefined ? `${it.delta >= 0 ? '+' : ''}${it.delta.toFixed(3)}` : it.status}</td>
            <td>{it.dimensionThatMoved ?? '—'}</td>
            <td>{it.correctnessFlip ? <span className="sd-down">{it.correctnessFlip.baseline}→{it.correctnessFlip.candidate}</span> : '—'}</td>
        </tr>
    );
}

function Card({ label, value, sub, cls }: { label: string; value: string; sub?: React.ReactNode; cls?: string }) {
    return (
        <div className="stat-card stat-card--fact">
            <div className="stat-card-label">{label}</div>
            <div className={`stat-card-value ${cls ?? ''}`}>{value}</div>
            {sub && <div className="stat-card-comment">{sub}</div>}
        </div>
    );
}

// ─── Config snapshot: flatten + diff ───────────────────────────────────

/** Top-level snapshot fields that aren't part of the resolved-behaviour
 *  material — excluded from the param view and diff (the fingerprint already
 *  hashes the rest; snapshotAt is just a timestamp). */
const VOLATILE_SNAPSHOT_KEYS = new Set(['configFingerprint', 'snapshotAt']);

/** Flatten a snapshot into `path → leaf value` entries (dot/index paths),
 *  dropping volatile top-level keys. Empty objects/arrays render as markers
 *  so a become-empty change still shows up. */
function flattenSnapshot(obj: unknown, prefix = '', out: Map<string, unknown> = new Map()): Map<string, unknown> {
    if (obj === null || typeof obj !== 'object') { out.set(prefix, obj); return out; }
    if (Array.isArray(obj)) {
        if (obj.length === 0) { out.set(prefix, '[]'); return out; }
        obj.forEach((v, i) => flattenSnapshot(v, prefix ? `${prefix}.${i}` : String(i), out));
        return out;
    }
    const entries = Object.entries(obj as Record<string, unknown>);
    if (entries.length === 0) { out.set(prefix, '{}'); return out; }
    for (const [k, v] of entries) {
        if (!prefix && VOLATILE_SNAPSHOT_KEYS.has(k)) continue;
        flattenSnapshot(v, prefix ? `${prefix}.${k}` : k, out);
    }
    return out;
}

function fmtLeaf(v: unknown): string {
    if (v === undefined) return '∅';
    if (typeof v === 'string') return v.length > 60 ? `${v.slice(0, 60)}…` : v;
    return String(v);
}

interface SnapshotDiffRow { path: string; before: unknown; after: unknown }
function diffSnapshots(a: ResolvedSnapshot, b: ResolvedSnapshot): SnapshotDiffRow[] {
    const fa = flattenSnapshot(a), fb = flattenSnapshot(b);
    const rows: SnapshotDiffRow[] = [];
    for (const path of [...new Set([...fa.keys(), ...fb.keys()])].sort()) {
        const before = fa.get(path), after = fb.get(path);
        if (before !== after) rows.push({ path, before, after });
    }
    return rows;
}

/** Field-level diff of two runs' resolved config snapshots — answers
 *  "what actually differs between these two runs". */
function ConfigDiff({ baseline, candidate }: { baseline: ScenarioEvalRun; candidate: ScenarioEvalRun }) {
    if (!baseline.snapshot || !candidate.snapshot) {
        return (
            <div className="sd-cfgdiff">
                <h4>Configuration diff</h4>
                <p className="sd-no">Config snapshot unavailable for one or both runs (pre-snapshot run).</p>
            </div>
        );
    }
    const rows = diffSnapshots(baseline.snapshot, candidate.snapshot);
    return (
        <div className="sd-cfgdiff">
            <h4>Configuration diff ({rows.length})</h4>
            {rows.length === 0
                ? <p className="sd-no">Identical resolved configuration — same prompts, providers, and params.</p>
                : (
                    <table className="dt sd-table">
                        <thead><tr><th>Parameter</th><th>{baseline.readableId} (baseline)</th><th>{candidate.readableId} (candidate)</th></tr></thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.path}>
                                    <td><code className="sd-fp">{r.path}</code></td>
                                    <td className="sd-down" title={typeof r.before === 'string' ? r.before : undefined}>{fmtLeaf(r.before)}</td>
                                    <td className="sd-up" title={typeof r.after === 'string' ? r.after : undefined}>{fmtLeaf(r.after)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
        </div>
    );
}

/** Per-run "all parameters" view — the run's frozen resolved snapshot. */
function ParamsModal({ run, onClose }: { run: ScenarioEvalRun; onClose: () => void }) {
    const snap = run.snapshot;
    // Resolve promptId → name/type (the snapshot pins only id+version).
    const { data: prompts } = useQuery({ queryKey: ['prompts', 'meta'], queryFn: () => adminApi.getPrompts({ includeArchived: true }) });
    const promptMeta = new Map((prompts ?? []).map(p => [p.id, p]));
    return (
        <div className="sd-overlay" onClick={onClose}>
            <div className="sd-modal sd-results" onClick={e => e.stopPropagation()}>
                <h2>Parameters — {run.readableId}</h2>
                {!snap ? <p className="sd-no">No config snapshot stored for this run.</p> : (
                    <>
                        <p className="sd-meta">
                            config <strong>{snap.pipelineConfigName}</strong> · code <code className="sd-fp">{snap.codeVersion?.slice(0, 10) || '—'}</code> · fingerprint <code className="sd-fp">{snap.configFingerprint.slice(0, 10)}</code>
                            {snap.modelIds?.length ? <> · models {snap.modelIds.join(', ')}</> : null}
                        </p>

                        <h4>Prompts</h4>
                        <table className="dt sd-table">
                            <thead><tr><th>Prompt</th><th>Type</th><th>Version</th></tr></thead>
                            <tbody>
                                {Object.values(snap.prompts ?? {}).map(p => {
                                    const meta = promptMeta.get(p.promptId);
                                    return (
                                        <tr key={p.promptId}>
                                            <td title={p.promptId}>{meta?.name ?? <code className="sd-fp">{p.promptId}</code>}</td>
                                            <td title={meta?.type}>{meta ? promptTypeLabel(meta.type) : '—'}</td>
                                            <td>v{p.version}</td>
                                        </tr>
                                    );
                                })}
                                {Object.keys(snap.prompts ?? {}).length === 0 && <tr><td colSpan={3} className="sd-empty">—</td></tr>}
                            </tbody>
                        </table>

                        <h4>Providers</h4>
                        <table className="dt sd-table">
                            <thead><tr><th>Provider</th><th>Model</th><th>Temp</th><th>Max tokens</th><th>Reasoning</th></tr></thead>
                            <tbody>
                                {Object.values(snap.providerDefaults ?? {}).map(pv => (
                                    <tr key={pv.providerKey}>
                                        <td><code className="sd-fp">{pv.providerKey}</code></td>
                                        <td>{pv.modelName}</td>
                                        <td>{pv.temperature ?? '—'}</td>
                                        <td>{pv.maxTokens ?? '—'}</td>
                                        <td>{pv.reasoningEffort ?? '—'}</td>
                                    </tr>
                                ))}
                                {Object.keys(snap.providerDefaults ?? {}).length === 0 && <tr><td colSpan={5} className="sd-empty">—</td></tr>}
                            </tbody>
                        </table>

                        <h4>Raw snapshot</h4>
                        <pre className="sd-rawjson">{JSON.stringify(snap, null, 2)}</pre>
                    </>
                )}
                <div className="sd-modal-actions"><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
            </div>
        </div>
    );
}
